import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UserDetails } from 'src/app/core/models/user-details.model';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { DeleteExpenseSurveyDocumentRequest, DeleteTimeSurveyDocumentRequest, DownloadSurveyDocumentRequest, SurveyDocument, TimeSurveyDocument } from 'src/app/survey/models/survey-document.model';
import { ToastrService } from '@kpmg-tax/ui';
import { Subscription } from 'rxjs';
import { EngagementInsigtsService } from 'src/app/engagement-insights/engagement-insights.service';
import { APP_CONSTANTS } from '../constants/app-constants';
import { Messages } from '../constants/messages';
import { role } from '../constants/role-enum';
import { isRcnSurveyApplication } from 'src/app/shared/lib/custom-tools';
import { FileService } from '../file-service/file.service';
import { BaseResponse } from '../models/base-response.model';
import { RightSideDrawerService } from '../right-side-drawer/right-side-drawer.service';
import { ExpenseSurveyDocumentService } from './services/expense-survey-document.service';
import { TimeSurveyDocumentService } from './services/time-survey-document.service';
import { ImpersonationService } from '../impersonation/impersonation.service';

const ZERO = 0;
const MINUS_ONE = -1;

@Component({
    selector: 'survey-document',
    templateUrl: './survey-document.component.html',
    styleUrls: ['./survey-document.component.scss']
})

export class SurveyDocumentComponent implements OnInit, OnDestroy {
    @Input() uploaderTitle!: string;
    @Input() surveyDocuments!: SurveyDocument[] | TimeSurveyDocument[];
    @Output() documentEvent = new EventEmitter<boolean>();
    subscriptions: Subscription[] = [];
    user: UserDetails;
    isClientManager = false;
    isClientManagerView = false;
    isArchived = false;
    showDocumentsList = false;
    uploadDescription = '';
    uploadType: string;
    isRcnSurveyApp: boolean = false;
    surveyEmployeeId = ZERO;
    surveyEmployeeName = '';
    studyId = ZERO;
    surveyId = ZERO;

    constructor(
        private _userDataService: UserDataService,
        private _toastrService: ToastrService,
        private _fileService: FileService,
        private _expenseSurveyDocumentService: ExpenseSurveyDocumentService,
        private _surveyDocumentService: TimeSurveyDocumentService,
        private _engagementService: EngagementInsigtsService,
        private _drawerService: RightSideDrawerService,
        private impersonationService: ImpersonationService,
    ) { }

    ngOnInit() {
        this.showDocumentsList = this.surveyDocuments.length > ZERO ? true : false;
        this.subscriptions.push(this.impersonationService.clientManagerModeActive$.subscribe(active => {
            this.isClientManagerView = active;
        }));

        this.subscriptions.push(this._userDataService.userDetails$.subscribe(userDetails => {
            if (userDetails) {
                this.user = userDetails;
                this.isClientManager = this.user.roleId === role.clientManager || this.isClientManagerView ? true : false;
            }
        }));

        this.subscriptions.push(this._drawerService.drawerOpen.subscribe(drawerData => {
            if (drawerData && drawerData.data) {
                this.surveyEmployeeId = drawerData.data.employeeId ? Number(drawerData.data.employeeId) : this.surveyEmployeeId;
                this.surveyEmployeeName = drawerData.data.employeeName ? drawerData.data.employeeName : this.surveyEmployeeName;
                this.studyId = drawerData.data.studyId ? Number(drawerData.data.studyId) : this.studyId;
            }
        }));

        this.isRcnSurveyApp = isRcnSurveyApplication();
    }

    downloadDocument(surveyDocument: SurveyDocument | TimeSurveyDocument) {
        if (this.isRcnSurveyApp) return;
        this.documentEvent.emit(true);
        this.uploadType = this.getUploadType();
        if (this.uploadType === APP_CONSTANTS.TIME_SURVEY) {
            const fileName = surveyDocument.fileName;
            const downloadTimeSurveyDocument = surveyDocument as TimeSurveyDocument;
            const downloadDoc: DownloadSurveyDocumentRequest = {
                documentId: downloadTimeSurveyDocument.documentId,
                engagementId: this.studyId,
                surveyType: this.uploadType,
                surveyId: downloadTimeSurveyDocument.surveyId
            };
            this.downloadTimeSurveyDocument(downloadDoc, fileName);
        }
        else if (this.uploadType === APP_CONSTANTS.EXPENSE_SURVEY) {
            const fileName = surveyDocument.fileName;
            const downloadExpenseSurveyDocument = surveyDocument as SurveyDocument;
            const downloadDoc: DownloadSurveyDocumentRequest = {
                documentId: downloadExpenseSurveyDocument.expenseSurveyDocumentId,
                engagementId: Number(downloadExpenseSurveyDocument.studyId),
                surveyType: this.uploadType,
                surveyId: downloadExpenseSurveyDocument.expenseSurveyId
            };
            this.downloadExpenseSurveyDocument(downloadDoc, fileName);
        }
    }

    getUploadType(): string {
        const timeSurveyUpload = APP_CONSTANTS.UPLOAD_TIME_SURVEY_DOCUMENT(this.surveyEmployeeName);
        switch (this.uploaderTitle.toLowerCase()) {
            case APP_CONSTANTS.UPLOAD_EXPENSE_SURVEY_DOCUMENT.toLowerCase():
                return APP_CONSTANTS.EXPENSE_SURVEY;
                break;
            case timeSurveyUpload.toLowerCase():
                return APP_CONSTANTS.TIME_SURVEY;
                break;
            case APP_CONSTANTS.UPLOAD_PROJECT_SURVEY_DOCUMENT.toLowerCase():
                 return APP_CONSTANTS.TIME_SURVEY;
                break;
            default:
                return APP_CONSTANTS.TIME_SURVEY;
        }
    }

    downloadExpenseSurveyDocument(downloadDocuments: DownloadSurveyDocumentRequest, fileName: string): void {
        this, this._expenseSurveyDocumentService.downloadExpenseSurveyDocument(downloadDocuments.documentId, downloadDocuments.surveyId, downloadDocuments.engagementId).subscribe(blob => {
            this.saveDocument(fileName, blob);
            this.documentEvent.emit(false);
        });
    }

    downloadTimeSurveyDocument(downloadDocuments: DownloadSurveyDocumentRequest, uploadedFileName: string): void {
        this._surveyDocumentService.downloadTimeSurveyDocument(downloadDocuments.documentId, downloadDocuments.surveyId, downloadDocuments.engagementId).subscribe(blobResponse => {
            if (blobResponse) {
                const fileName: string = uploadedFileName;
                this.saveDocument(`${fileName}`,blobResponse);
            }
            this.documentEvent.emit(false);
        });
    }

    saveDocument(file: string, blob: Blob): void {
        let fileName1 = 'Documents';
        let extention = '';
        extention = '.' + file.split('/').pop().split('.').pop();
        extention= extention===".msg"?'.zip':extention;
        fileName1 = file.split('/').pop().split('.').slice(ZERO, MINUS_ONE).join('.');
        const fileName = decodeURIComponent(fileName1);
        this._fileService.saveBlobToFile(blob, `${fileName}${extention}`);
    }

    deleteDocument(document: SurveyDocument | TimeSurveyDocument) {
        this.uploadType = this.getUploadType();
        if (this.uploadType === APP_CONSTANTS.TIME_SURVEY) {
            const surveyDocument = document as TimeSurveyDocument;
            this.deleteTimeSurveyDocument(surveyDocument);
        } else if (this.uploadType === APP_CONSTANTS.EXPENSE_SURVEY) {
            const surveyDocument = document as SurveyDocument;
            this.deleteExpenseSurveyDocument(surveyDocument);
        }
    }

    deleteExpenseSurveyDocument(surveyDocument: SurveyDocument) {
        this.documentEvent.emit(true);
        const deleteDocumentRequest: DeleteExpenseSurveyDocumentRequest =
        {
            expenseSurveyDocumentId: surveyDocument.expenseSurveyDocumentId,
            engagementId: this.studyId,
            expenseSurveyId: surveyDocument.expenseSurveyId
        };

        this._expenseSurveyDocumentService.deleteExpensedocument(deleteDocumentRequest).subscribe((data: BaseResponse) => {
            if (data !== null && data.success) {
                this._expenseSurveyDocumentService.getSurveyDocuments(surveyDocument.studyId, deleteDocumentRequest.expenseSurveyId).subscribe((response: BaseResponse) => {
                    const surveyDocumentsresponse = response.result as SurveyDocument[];
                    surveyDocumentsresponse.map(item => item.studyId = this.studyId);
                    this._expenseSurveyDocumentService.setSurveyDocuments(surveyDocumentsresponse);
                    this.surveyDocuments = surveyDocumentsresponse;
                    this._engagementService.refreshExpenseSurveyDocumentCount(true);
                    this._toastrService.setSuccessMessage(Messages.Survey.UploadDocument.DeleteDocumentSuccess);
                    this.documentEvent.emit(false);
                });
            }
        });
    }

    deleteTimeSurveyDocument(surveyDocument: TimeSurveyDocument) {
        this.documentEvent.emit(true);
        const deleteDocumentRequest: DeleteTimeSurveyDocumentRequest =
        {
            documentId: surveyDocument.documentId,
            engagementId: Number(surveyDocument.studyId),
            surveyId: surveyDocument.surveyId,
            employeeId: this.surveyEmployeeId
        };
        this._surveyDocumentService.deleteTimeSurveyDocument(deleteDocumentRequest).subscribe(response => {
            if (response) {
                this._surveyDocumentService.getEmployeeDocuments(this.studyId, surveyDocument.surveyId,
                    surveyDocument.employeeId).subscribe((response: BaseResponse) => {
                        if (response) {
                            const surveyDocumentsResponse = response.result as TimeSurveyDocument[];
                            surveyDocumentsResponse.map(item => item.studyId = this.studyId);
                            this._surveyDocumentService.setTimeSurveyDocumentUpload(surveyDocumentsResponse);
                            this.surveyDocuments = surveyDocumentsResponse;
                            this.documentEvent.emit(false);
                        }
                    });
                this._toastrService.setSuccessMessage(Messages.Survey.UploadDocument.DeleteDocumentSuccess);
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => {
            if (subscription) {
                subscription.unsubscribe();
            }
        });
    }
}
